import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AgentExecutionTimeline } from '../../../components/AgentPlatform/AgentExecutionTimeline';
import { AgentPlatformAdminGate } from '../../../components/AgentPlatform/AgentPlatformAdminGate';
import { AgentPlatformFeatureGate } from '../../../components/AgentPlatform/AgentPlatformFeatureGate';
import { AgentPlatformPageHeader } from '../../../components/AgentPlatform/AgentPlatformPageHeader';
import { AgentPlatformPanel } from '../../../components/AgentPlatform/AgentPlatformPanel';
import { AgentPlatformShell } from '../../../components/AgentPlatform/AgentPlatformShell';
import { StatusBadge } from '../../../components/AgentPlatform/StatusBadge';
import { Button } from '../../../components/UI/Button';
import { agentPlatformService } from '../../../services/agentPlatformService';
import type {
  AgentDto,
  AgentExecutionDto,
  AgentExecutionListItem,
  AgentExecutionMonitoringSummary,
  AgentExecutionStatus,
} from '../../../types/agentPlatform';
import { labelExecutionStatus } from '../../../utils/agentPlatformLabels';
import { apTheme } from '../../../utils/agentPlatformTheme';

export const AgentExecutionsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const highlightId = searchParams.get('highlight');

  const [items, setItems] = useState<AgentExecutionListItem[]>([]);
  const [summary, setSummary] = useState<AgentExecutionMonitoringSummary | null>(null);
  const [selected, setSelected] = useState<AgentExecutionDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [testOnly, setTestOnly] = useState<boolean | undefined>(undefined);
  const [agents, setAgents] = useState<AgentDto[]>([]);
  const [agentId, setAgentId] = useState('');
  const [status, setStatus] = useState<AgentExecutionStatus | ''>('');
  const [correlationId, setCorrelationId] = useState('');

  useEffect(() => {
    agentPlatformService.getAgents().then(setAgents).catch(() => undefined);
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [result, sum] = await Promise.all([
        agentPlatformService.searchExecutions({
          page,
          pageSize: 15,
          isTestExecution: testOnly,
          agentId: agentId || undefined,
          status: status || undefined,
          correlationId: correlationId.trim() || undefined,
        }),
        agentPlatformService.getExecutionSummary({
          isTestExecution: testOnly,
          agentId: agentId || undefined,
        }),
      ]);
      setItems(result.data);
      setTotalPages(result.totalPages);
      setSummary(sum);

      if (highlightId) {
        const detail = await agentPlatformService.getExecution(highlightId);
        setSelected(detail);
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [page, testOnly, highlightId, agentId, status, correlationId]);

  const openDetail = async (id: string) => {
    const detail = await agentPlatformService.getExecution(id);
    setSelected(detail);
  };

  return (
    <AgentPlatformFeatureGate feature="platform">
      <AgentPlatformAdminGate>
        <AgentPlatformShell>
        <AgentPlatformPageHeader
          title="Execuções"
          subtitle="Histórico, tokens e steps de cada run."
          area="operations"
        />

        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <StatCard label="Total" value={summary.totalExecutions} />
            <StatCard label="Concluídas" value={summary.completedCount} />
            <StatCard label="Falhas" value={summary.failedCount} />
            <StatCard label="Tokens" value={summary.totalTokens} />
          </div>
        )}

        <AgentPlatformPanel className="mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-slate-500 mb-1 font-medium">Agente</label>
            <select
              className={apTheme.input}
              value={agentId}
              onChange={e => {
                setAgentId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">Todos</option>
              {agents.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1 font-medium">Status</label>
            <select
              className={apTheme.input}
              value={status}
              onChange={e => {
                setStatus(e.target.value as AgentExecutionStatus | '');
                setPage(1);
              }}
            >
              <option value="">Todos</option>
              <option value="Running">Em execução</option>
              <option value="Completed">Concluída</option>
              <option value="Failed">Falhou</option>
              <option value="WaitingExternal">Aguardando externo</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1 font-medium">Correlation ID</label>
            <input
              className={`${apTheme.input} font-mono`}
              value={correlationId}
              onChange={e => setCorrelationId(e.target.value)}
              placeholder="Buscar…"
            />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <Button
            variant={testOnly === undefined ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setTestOnly(undefined)}
          >
            Todas
          </Button>
          <Button
            variant={testOnly === true ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setTestOnly(true)}
          >
            Só testes
          </Button>
          <Button
            variant={testOnly === false ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setTestOnly(false)}
          >
            Produção
          </Button>
        </div>
        </AgentPlatformPanel>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className={apTheme.tableWrap}>
            <table className="min-w-full divide-y divide-slate-100 text-sm">
              <thead>
                <tr className={apTheme.tableHead}>
                  <th className="px-3 py-2 text-left">Agente</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Quando</th>
                  <th className="px-3 py-2 text-right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {loading && (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                      Carregando…
                    </td>
                  </tr>
                )}
                {items.map(row => (
                  <tr
                    key={row.id}
                    className={`cursor-pointer transition-colors hover:bg-red-50/40 ${
                      selected?.id === row.id ? 'bg-red-50/60' : ''
                    }`}
                    onClick={() => openDetail(row.id)}
                    onDoubleClick={() => navigate(`/agent-platform/executions/${row.id}`)}
                  >
                    <td className="px-3 py-2">
                      <div className="font-medium">{row.agentName ?? row.agentCode}</div>
                      {row.isTestExecution && (
                        <span className="text-xs text-amber-700">teste</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge
                        status={row.status}
                        label={labelExecutionStatus(row.status)}
                        kind="execution"
                      />
                    </td>
                    <td className="px-3 py-2 text-slate-500 text-xs">
                      {new Date(row.startedAt).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={`/agent-platform/executions/${row.id}`}
                        className="text-red-600 text-xs hover:underline font-medium"
                        onClick={e => e.stopPropagation()}
                      >
                        abrir
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-between px-3 py-2 border-t border-slate-100 bg-slate-50/50 text-sm">
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                Anterior
              </Button>
              <span className="text-slate-500">
                {page} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>

          <AgentPlatformPanel title="Detalhe">
            {!selected && <p className="text-sm text-slate-500">Selecione uma execução.</p>}
            {selected && (
              <div className="space-y-3 text-sm">
                <p>
                  <strong className="text-slate-700">ID:</strong>{' '}
                  <span className="font-mono text-xs text-slate-600">{selected.id}</span>
                </p>
                <p>
                  <strong className="text-slate-700">Status:</strong> {labelExecutionStatus(selected.status)}
                </p>
                <p>
                  <strong className="text-slate-700">Tokens:</strong> {selected.totalTokens}
                </p>
                {selected.errorMessage && (
                  <p className="text-red-700">{selected.errorMessage}</p>
                )}
                <AgentExecutionTimeline steps={selected.steps} />
                <Link
                  to={`/agent-platform/executions/${selected.id}`}
                  className="text-red-600 hover:underline inline-block font-medium"
                >
                  Ver página completa →
                </Link>
              </div>
            )}
          </AgentPlatformPanel>
        </div>
        </AgentPlatformShell>
      </AgentPlatformAdminGate>
    </AgentPlatformFeatureGate>
  );
};

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className={apTheme.statCard}>
      <p className="text-xs text-slate-500 uppercase font-semibold tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-slate-900 mt-1">{value.toLocaleString('pt-BR')}</p>
    </div>
  );
}
